/**
 * 省略表記に値をマッピングする
 *
 * 例：{"jp": "日本語", "en": "英語"}
 *     Mapping().get("en") -> 英語
 *
 * 例：{true: "表示", false: "非表示"}
 *     Mapping().get(false) -> 非表示
 */
class Mapping {
  _defines = {};
  constructor(defines) {
    this._defines = defines;
  }

  /**
   * 割り当てた値を取得する
   * @param {省略キー} key
   * @returns 割り当てた値
   */
  get(key) {
    if (key === undefined) {
      return undefined;
    }
    if (key in this._defines) {
      return this._defines[key];
    }
    return undefined;
  }

  /**
   * 割り当てた値を取得、未割り当てであればデフォルト値を取得する
   * @param {省略キー} key
   * @param {未割当時の値} defaults
   * @returns 割り当てた値
   */
  getOrElse(key, defaults) {
    const result = this.get(key);
    if (result === undefined) {
      return defaults;
    }
    return result;
  }
}

/**
 * 値の合計を返す
 *
 * 例：[10, 99, -1], 未定義値：-1
 *     Sum().get() -> 109
 *
 * 例：[10, 99, -1], 未定義値：99
 *     Sum().get() -> 9
 */
class Sum {
  _values = [];
  _whenUndefined = -1;
  constructor(values, whenUndefined) {
    this._values = values;
    this._whenUndefined = whenUndefined;
  }

  /**
   * 合計を取得する
   * @returns 合計
   */
  get() {
    let result = 0;
    let containsAnyValue = false;
    for (const value of this._values) {
      if (value != this._whenUndefined) {
        containsAnyValue = true;
        result += value;
      }
    }
    if (containsAnyValue) {
      return result;
    }
    return undefined;
  }

  /**
   * 合計を取得、全ての値が未定義値であればデフォルト値を取得する
   * @param {未定義時の値} defaults
   * @returns 合計
   */
  getOrElse(defaults) {
    const result = this.get();
    if (result === undefined) {
      return defaults;
    }
    return result;
  }

  /**
   * 合計を取得、返す文字列があれば引数の関数で変換する
   * @param {合計を引数にする関数} functions
   * @returns 関数で変換した文字列
   */
  present(functions) {
    const result = this.get();
    if (result === undefined) {
      return undefined;
    }
    return functions(result);
  }
}

/**
 * 文字列を結合する。conditionを指定する場合はtrueの文字列だけを結合する
 *
 * 例：["りんご","みかん","ぶどう"], 区切り文字：,
 *     Join().get() -> りんご,みかん,ぶどう
 *
 * 例：[
 *       {"text": "りんご", "condition": true},
 *       {"text": "みかん", "condition": false},
 *       {"text": "ぶどう", "condition": true}
 *     ], 区切り文字：,
 *
 *     Join().get() -> りんご,ぶどう
 */
class Join {
  _values = [];
  _separator = ",";
  constructor(values, separator) {
    this._values = values;
    this._separator = separator;
  }

  /**
   * 値をパース、conditionがあれば状態を検証する
   * @param {引数の値} value
   * @returns 結合してよい文字列であれば文字列を返す
   */
  parseValue(value) {
    if (value === undefined) {
      return undefined;
    }
    if (typeof value == "string" && value.length >= 1) {
      return value;
    }
    if (typeof value == "object" && "condition" in value) {
      if (value["condition"]) {
        return value["value"];
      }
    }
    return undefined;
  }

  /**
   * 区切り文字で結合した文字列を返す
   * @returns 区切り文字で結合した文字列
   */
  get() {
    let results = [];
    for (const value of this._values) {
      const input = this.parseValue(value);
      if (input !== undefined) {
        results.push(input);
      }
    }
    if (results.length == 0) {
      return undefined;
    }
    return results.join(this._separator);
  }

  /**
   * 区切り文字で結合した文字列、返す文字列があれば引数の関数で変換する
   * @param {区切り文字で結合した文字列を引数にする関数} functions
   * @returns 関数で変換した文字列
   */
  present(functions) {
    const result = this.get();
    if (result === undefined) {
      return undefined;
    }
    return functions(result);
  }

  /**
   * 区切り文字で結合した文字列、返す文字列がなければデフォルト値を取得する
   * @param {返却なし時の値} defaults
   * @returns 区切り文字で結合した文字列
   */
  getOrElse(defaults) {
    const result = this.get();
    if (result === undefined) {
      return defaults;
    }
    return result;
  }
}

/**
 * 値のマッピング、整形をする
 */
class ValueMapper {
  /**
   * 省略表記に値をマッピングする
   *
   * 例：{"jp": "日本語", "en": "英語"}
   *     Mapping().get("en") -> 英語
   *
   * 例：{true: "表示", false: "非表示"}
   *     Mapping().get(false) -> 非表示
   */
  map(defines) {
    return new Mapping(defines);
  }

  /**
   * 値の合計を返す
   *
   * 例：[10, 99, -1], 未定義値：-1
   *     Sum().get() -> 109
   *
   * 例：[10, 99, -1], 未定義値：99
   *     Sum().get() -> 9
   */
  sum(values, whenUndefined) {
    return new Sum(values, whenUndefined);
  }

  /**
   * 文字列を結合する。conditionを指定する場合はtrueの文字列だけを結合する
   *
   * 例：["りんご","みかん","ぶどう"], 区切り文字：,
   *     Join().get() -> りんご,みかん,ぶどう
   *
   * 例：[
   *       {"text": "りんご", "condition": true},
   *       {"text": "みかん", "condition": false},
   *       {"text": "ぶどう", "condition": true}
   *     ], 区切り文字：,
   *
   *     Join().get() -> りんご,ぶどう
   */
  join(values, separator) {
    return new Join(values, separator);
  }

  /**
   * 文字列を直接返却する
   *
   * 例：
   *     string("りんご").get() -> りんご
   */
  string(value) {
    return new Join([value], "");
  }
}

export default new ValueMapper();
